import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { Container } from '@/components/ui/Container';
import { Reveal } from '@/motion/Reveal';
import { Breadcrumbs } from './Breadcrumbs';
import type { Crumb } from './Breadcrumbs';

export interface PageHeroProps {
  /** Small uppercase brass label above the title. */
  eyebrow?: ReactNode;
  /** The page's one `<h1>`. */
  title: ReactNode;
  /** One or two sentences under the title. */
  lead?: ReactNode;
  breadcrumbs?: readonly Crumb[];
  /** Buttons / links under the lead. */
  actions?: ReactNode;
  /** `start` (default) or `center`. */
  align?: 'start' | 'center';
  /** Extra content on the right (an image, a figure). Stacks below the copy on small screens. */
  aside?: ReactNode;
  className?: string;
  /** Less air above and below — for pages whose content starts right away (the shop). */
  compact?: boolean;
  /**
   * A picture or video that belongs to the hero itself (`/process`). It is rendered before the copy, inside the
   * header; the page lays it out (behind the copy on wide screens, above it on small ones) with its own CSS.
   */
  media?: ReactNode;
  /** Element id for the heading (for `aria-labelledby` from the page). */
  titleId?: string;
}

/**
 * The opening of every inner page: breadcrumbs, eyebrow, the `<h1>`, a lead and optional actions, set
 * on a pool of polished-stone light and grain. It slides up under the transparent header the way the
 * home hero does, so pages feel like one continuous site. Content resolves with the standard reveal.
 */
export function PageHero({
  eyebrow,
  title,
  lead,
  breadcrumbs,
  actions,
  align = 'start',
  aside,
  compact = false,
  media,
  className,
  titleId,
}: PageHeroProps) {
  return (
    <header
      className={clsx('page-hero', className)}
      data-align={align}
      data-size={compact ? 'compact' : undefined}
      data-media={media ? 'true' : undefined}
    >
      {media}
      <Container>
        <div className="page-hero__grid" data-has-aside={aside ? 'true' : undefined}>
          <div className="page-hero__copy">
            {breadcrumbs && breadcrumbs.length > 0 ? <Breadcrumbs items={breadcrumbs} /> : null}
            {eyebrow ? (
              <Reveal as="p" variant="focus" className="t-eyebrow">
                {eyebrow}
              </Reveal>
            ) : null}
            <Reveal as="h1" id={titleId} className="page-hero__title t-h1">
              {title}
            </Reveal>
            {lead ? (
              <Reveal as="p" delay={120} className="page-hero__lead t-lead text-fg-muted">
                {lead}
              </Reveal>
            ) : null}
            {actions ? (
              <Reveal delay={220} className="page-hero__actions">
                {actions}
              </Reveal>
            ) : null}
          </div>
          {aside ? <div className="page-hero__aside">{aside}</div> : null}
        </div>
      </Container>
    </header>
  );
}
